// src/pages/reports/IrAnalysis/ReportIrTrFilter.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Checkbox,
  CircularProgress,
  InputAdornment,
  GlobalStyles,
} from "@mui/material";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { getToken } from "../../../utils/utils";
import { getReportsFilterOptions_IrTrans } from "../api";
import { getalllocations, getalloutlettype } from "../../assign-task/api";

import arrowLeft from "../../../assests/images/arrow-left.svg";
import arrowRight from "../../../assests/images/arrow-right.png";
import doubleArrowLeft from "../../../assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../assests/images/double-arrow-right.svg";

const SELECT_ALL_LABEL = "Select All";

const CustomArrow = ({ open, onClick }: { open?: boolean; onClick?: () => void }) => (
  <span
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRight: "2px solid #222",
      borderBottom: "2px solid #222",
      transform: open ? "rotate(-135deg)" : "rotate(45deg)",
      transformOrigin: "center center",
      transition: "transform 0.18s ease",
      marginLeft: 12,
      position: "relative",
      cursor: "pointer",
      boxSizing: "border-box",
    }}
  />
);

// === helpers identical to ReportsFilterPopup (UI only) ===
const CLEAR_X_DATA_URI =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
  );

const btnResetStyle: React.CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const renderHeaderWithIcons = ({
  date,
  decreaseMonth,
  increaseMonth,
  decreaseYear,
  increaseYear,
}: {
  date: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  decreaseYear: () => void;
  increaseYear: () => void;
}) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
    <button type="button" onClick={decreaseYear} aria-label="Previous year" style={btnResetStyle}>
      <img src={doubleArrowLeft} alt="Previous year" />
    </button>
    <button type="button" onClick={decreaseMonth} aria-label="Previous month" style={btnResetStyle}>
      <img src={arrowLeft} alt="Previous month" />
    </button>

    <span style={{ margin: "0 10px", fontWeight: 600 }}>
      {date.toLocaleString("default", { year: "numeric", month: "long" })}
    </span>

    <button type="button" onClick={increaseMonth} aria-label="Next month" style={btnResetStyle}>
      <img src={arrowRight} alt="Next month" />
    </button>
    <button type="button" onClick={increaseYear} aria-label="Next year" style={btnResetStyle}>
      <img src={doubleArrowRight} alt="Next year" />
    </button>
  </div>
);

const ForwardedTextInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick, placeholder, style, sx, ...rest }, ref) => {
    const mergedStyle = { paddingRight: 40, ...(style || {}), width: "100%" };
    return (
      <TextField
        size="small"
        variant="outlined"
        inputRef={ref}
        onClick={onClick}
        value={value}
        placeholder={placeholder || "Select date range"}
        fullWidth
        {...rest}
        sx={{ position: "relative", width: "100%", ...(sx || {}) }}
        InputProps={{
          ...(rest.InputProps || {}),
          endAdornment: (
            <>
              {rest.InputProps?.endAdornment}
              <InputAdornment position="end" sx={{ position: "absolute", right: 10 }}>
                <CustomArrow
                  open={false}
                  onClick={() => {
                    try {
                      onClick && (onClick as unknown as () => void)();
                    } catch {
                      try {
                        onClick?.();
                      } catch {}
                    }
                  }}
                />
              </InputAdornment>
            </>
          ),
        }}
        inputProps={{
          ...(rest.inputProps || {}),
          style: mergedStyle,
        }}
      />
    );
  }
);
ForwardedTextInput.displayName = "ForwardedTextInput";

const formatRangeText = (from: Date | null, to: Date | null) => {
  if (!from && !to) return "Select date range";
  const s = from ? from.toLocaleDateString() : "";
  const e = to ? to.toLocaleDateString() : "";
  return s && e ? `${s} - ${e}` : s || e || "Select date range";
};

// 🔧 local YYYY-MM-DD (prevents UTC shift)
const formatLocalYMD = (d: Date | null) => {
  if (!d || isNaN(d.getTime())) return null;
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

// === SearchableMultiSelect unchanged ===
function SearchableMultiSelect({
  label,
  options,
  value,
  onChange,
  loading,
  formatOptionLabel,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (vals: string[]) => void;
  loading?: boolean;
  formatOptionLabel?: (s: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const fmt = (s: string) => (formatOptionLabel ? formatOptionLabel(s) : s);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearchValue("");
      }
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && (setOpen(false), setSearchValue(""));
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const filtered = (options || []).filter((opt) =>
    fmt(opt).toLowerCase().includes(searchValue.toLowerCase())
  );

  const allSelected = value.length > 0 && value.length === options.length;
  const displayedValue =
    value.length === 0 ? label : value.length <= 2 ? value.map(fmt).join(", ") : `${value.slice(0, 2).map(fmt).join(", ")}…`;

  const toggleOption = (opt: string) =>
    value.includes(opt) ? onChange(value.filter((v) => v !== opt)) : onChange([...value, opt]);

  const toggleSelectAll = () => (allSelected ? onChange([]) : onChange(options.slice()));

  return (
    <Box ref={containerRef} sx={{ width: "100%", position: "relative", userSelect: "none" }}>
      <TextField
        size="small"
        variant="outlined"
        value={displayedValue}
        onClick={() => setOpen((v) => !v)}
        placeholder={label}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <>
              {loading ? <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} /> : null}
              <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                <CustomArrow open={open} onClick={() => setOpen((v) => !v)} />
              </InputAdornment>
            </>
          ),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        fullWidth
      />

      {open && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            maxHeight: 250,
            overflowY: "auto",
            border: "1px solid #ccc",
            backgroundColor: "#fff",
            zIndex: 3500,
            mt: 0.5,
            borderRadius: 1,
            boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 1, py: 0.5 }}>
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              style={{
                width: "100%",
                padding: "10px 10px",
                borderRadius: 4,
                border: "1px solid #ddd",
                fontSize: "0.9rem",
                marginBottom: 8,
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "5px",
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </Box>

          <Box sx={{ px: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1, cursor: "pointer" }} onClick={toggleSelectAll}>
              <Checkbox checked={allSelected} indeterminate={value.length > 0 && !allSelected} />
              <Typography variant="body2">{SELECT_ALL_LABEL}</Typography>
            </Box>

            {filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
                No options found
              </Typography>
            )}

            {filtered.map((opt) => {
              const checked = value.includes(opt);
              return (
                <Box
                  key={opt}
                  sx={{ display: "flex", alignItems: "center", py: 0.5, cursor: "pointer" }}
                  onClick={() => toggleOption(opt)}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Checkbox checked={checked} />
                  <Typography variant="body2">{fmt(opt)}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ==== Props (STRICT IR - Transactions) ====
export interface ReportIrTrFilterProps {
  isOpen?: boolean;
  shouldFetch?: boolean;
  onFetched?: () => void;

  initialFilters?: Partial<{
    locations: string[] | null;
    outlet_category: string[] | null;
    pmi_entity_code: string[] | null;
    outlet_name: string[] | null;
    transaction_from: string | null; // "YYYY-MM-DD"
    transaction_to: string | null;   // "YYYY-MM-DD"
  }> | null;

  onApplyFilters: (filters: {
    locations: string[];
    outlet_category: string[];
    pmi_entity_code: string[];
    outlet_name: string[];
    transaction_from?: string | null;
    transaction_to?: string | null;
  }) => void;

  onCancel?: () => void;
}

export default function ReportIrTrFilter({
  isOpen = false,
  shouldFetch = false,
  onFetched,
  initialFilters = null,
  onApplyFilters,
  onCancel,
}: ReportIrTrFilterProps) {
  const token = getToken();

  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevLeft = document.body.style.left;
    const prevRight = document.body.style.right;
    const prevOverflow = document.body.style.overflow;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.position = prevPosition || "";
      document.body.style.top = prevTop || "";
      document.body.style.left = prevLeft || "";
      document.body.style.right = prevRight || "";
      document.body.style.overflow = prevOverflow || "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  // Options
  const [locationOptions, setLocationOptions] = useState<string[]>([]);
  const [outletCategoryOptions, setOutletCategoryOptions] = useState<string[]>([]);
  const [pmiOptions, setPmiOptions] = useState<string[]>([]);
  const [outletNameOptions, setOutletNameOptions] = useState<string[]>([]);

  // Selections
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedOutletCategories, setSelectedOutletCategories] = useState<string[]>([]);
  const [selectedPmi, setSelectedPmi] = useState<string[]>([]);
  const [selectedOutletNames, setSelectedOutletNames] = useState<string[]>([]);

  // Transaction date range
  const [transFrom, setTransFrom] = useState<Date | null>(null);
  const [transTo, setTransTo] = useState<Date | null>(null);

  // Loading flags
  const [loadingLocCat, setLoadingLocCat] = useState(false);
  const [loadingPmiOutlet, setLoadingPmiOutlet] = useState(false);

  // Abort & cache
  const pmiOutletAbortRef = useRef<AbortController | null>(null);
  const onFetchedCalledRef = useRef(false);
  const cacheRef = useRef<Record<string, { pmi: string[]; outlets: string[]; expiry: number }>>(
    {}
  );
  const TTL_MS = 60 * 1000;

  // Stable keys (include date range so lists reflect txn filter)
  const locationsKey = useMemo(
    () => (Array.isArray(selectedLocations) ? [...selectedLocations].sort().join("|") : ""),
    [selectedLocations]
  );
  const outletNamesKey = useMemo(
    () => (Array.isArray(selectedOutletNames) ? [...selectedOutletNames].sort().join("|") : ""),
    [selectedOutletNames]
  );
  const pmiKey = useMemo(
    () => (Array.isArray(selectedPmi) ? [...selectedPmi].sort().join("|") : ""),
    [selectedPmi]
  );
  const categoryKey = useMemo(
    () => (Array.isArray(selectedOutletCategories) ? [...selectedOutletCategories].sort().join("|") : ""),
    [selectedOutletCategories]
  );

  // use local Y-M-D to avoid UTC off-by-one
  const transKey = useMemo(() => {
    const fs = formatLocalYMD(transFrom) || "";
    const ts = formatLocalYMD(transTo) || "";
    return `${fs}__${ts}`;
  }, [transFrom, transTo]);

  const arraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);

  // Initial fetch for Locations & Outlet Categories
  useEffect(() => {
    if (!isOpen || !shouldFetch) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLocCat(true);
        const [locRes, catRes] = await Promise.allSettled([getalllocations(), getalloutlettype()]);

        if (cancelled) return;

        if (locRes.status === "fulfilled") {
          const val: any = locRes.value;
          const arr =
            (Array.isArray(val?.data?.data) && val.data.data) ||
            (Array.isArray(val?.data) && val.data) ||
            (Array.isArray(val?.Data) && val.Data) ||
            (Array.isArray(val) && val) ||
            [];
          setLocationOptions(arr.map((s: any) => String(s ?? "").trim()).filter(Boolean));
        } else {
          setLocationOptions([]);
        }

        if (catRes.status === "fulfilled") {
          const val: any = catRes.value;
          const arr =
            (Array.isArray(val?.data?.data) && val.data.data) ||
            (Array.isArray(val?.data) && val.data) ||
            (Array.isArray(val?.Data) && val.Data) ||
            (Array.isArray(val) && val) ||
            [];
          setOutletCategoryOptions(arr.map((s: any) => String(s ?? "").trim()).filter(Boolean));
        } else {
          setOutletCategoryOptions([]);
        }

        if (!cancelled && !onFetchedCalledRef.current) {
          onFetchedCalledRef.current = true;
          onFetched?.();
        }
      } catch {
        if (!cancelled) {
          setLocationOptions([]);
          setOutletCategoryOptions([]);
        }
      } finally {
        if (!cancelled) setLoadingLocCat(false);
      }
    })();

    return () => {
      cancelled = true;
      onFetchedCalledRef.current = false;
    };
  }, [isOpen, shouldFetch, onFetched]);

  // Interdependent fetch for PMI & Outlet Names (and Categories) – includes transaction date
  useEffect(() => {
    if (!isOpen) return;

    if (pmiOutletAbortRef.current) {
      try {
        pmiOutletAbortRef.current.abort();
      } catch {}
    }
    const ctrl = new AbortController();
    pmiOutletAbortRef.current = ctrl;

    (async () => {
      try {
        setLoadingPmiOutlet(true);

        const now = Date.now();
        const hasOutletFilter = selectedOutletNames.length > 0;
        const hasPmiFilter = selectedPmi.length > 0;

        // Only cache on locations + date when NO PMI/Outlet/Category narrowing
        const allowCache =
          !hasOutletFilter && !hasPmiFilter && selectedOutletCategories.length === 0;
        const cacheKey = `${locationsKey}__${categoryKey}__${transKey}`;

        if (allowCache) {
          const cached = cacheRef.current[cacheKey];
          if (cached && cached.expiry > now) {
            setPmiOptions(cached.pmi);
            setOutletNameOptions(cached.outlets);

            setSelectedPmi((prev) => {
              const pruned = prev.filter((p) => cached.pmi.includes(p));
              return arraysEqual(pruned, prev) ? prev : pruned;
            });
            setSelectedOutletNames((prev) => {
              const pruned = prev.filter((n) => cached.outlets.includes(n));
              return arraysEqual(pruned, prev) ? prev : pruned;
            });
            return;
          }
        }

        const transFromStr = formatLocalYMD(transFrom);
        const transToStr = formatLocalYMD(transTo);

        const fo = await getReportsFilterOptions_IrTrans(
          {
            locations: selectedLocations.length ? selectedLocations : null,
            outlet_name: hasOutletFilter ? selectedOutletNames : null,
            pmi_entity_code: hasPmiFilter ? selectedPmi : null,
            outlet_category: selectedOutletCategories.length ? selectedOutletCategories : null,
            transaction_from: transFromStr,
            transaction_to: transToStr,
          },
          token,
          ctrl.signal
        );

        if (ctrl.signal.aborted) return;

        const pmiList = Array.isArray(fo?.pmiEntityCodes) ? fo.pmiEntityCodes : [];
        const outletList = Array.isArray(fo?.outletNames) ? fo.outletNames : [];
        const categoryList = Array.isArray(fo?.outletCategories) ? fo.outletCategories : [];

        setPmiOptions(pmiList);
        setOutletNameOptions(outletList);

        // always set categories and prune selected categories
        setOutletCategoryOptions(categoryList);
        setSelectedOutletCategories((prev) => {
          const pruned = prev.filter((c) => categoryList.includes(c));
          return arraysEqual(pruned, prev) ? prev : pruned;
        });

        if (allowCache) {
          cacheRef.current[cacheKey] = {
            pmi: pmiList,
            outlets: outletList,
            expiry: now + TTL_MS,
          };
        }

        // prune selections against fresh options
        setSelectedPmi((prev) => {
          const pruned = prev.filter((p) => pmiList.includes(p));
          return arraysEqual(pruned, prev) ? prev : pruned;
        });
        setSelectedOutletNames((prev) => {
          const pruned = prev.filter((n) => outletList.includes(n));
          return arraysEqual(pruned, prev) ? prev : pruned;
        });
      } catch {
        if (!ctrl.signal.aborted) {
          setPmiOptions([]);
          setOutletNameOptions([]);
          // keep categories from initial load if API fails
        }
      } finally {
        if (pmiOutletAbortRef.current === ctrl) pmiOutletAbortRef.current = null;
        setLoadingPmiOutlet(false);
      }
    })();

    return () => {
      if (pmiOutletAbortRef.current) {
        try {
          pmiOutletAbortRef.current.abort();
        } catch {}
        pmiOutletAbortRef.current = null;
      }
    };
  }, [
    isOpen,
    locationsKey,
    outletNamesKey,
    pmiKey,
    categoryKey, // added
    token,
    transKey,
    selectedOutletCategories,
  ]);

  // Rehydrate from initialFilters (once per open)
  useEffect(() => {
    if (!initialFilters) return;

    setSelectedLocations(initialFilters.locations ?? []);
    setSelectedOutletCategories(initialFilters.outlet_category ?? []);
    setSelectedPmi(initialFilters.pmi_entity_code ?? []);
    setSelectedOutletNames(initialFilters.outlet_name ?? []);

    if (initialFilters.transaction_from) {
      const d = new Date(initialFilters.transaction_from);
      setTransFrom(isNaN(d.getTime()) ? null : d);
    } else setTransFrom(null);

    if (initialFilters.transaction_to) {
      const d = new Date(initialFilters.transaction_to);
      setTransTo(isNaN(d.getTime()) ? null : d);
    } else setTransTo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFilters]);

  const handleClear = () => {
    setSelectedLocations([]);
    setSelectedOutletCategories([]);
    setSelectedPmi([]);
    setSelectedOutletNames([]);
    setTransFrom(null);
    setTransTo(null);

    onApplyFilters({
      locations: [],
      outlet_category: [],
      pmi_entity_code: [],
      outlet_name: [],
      transaction_from: null,
      transaction_to: null,
    });
    onCancel?.();
  };

  const handleApply = () => {
    const fromStr = formatLocalYMD(transFrom);
    const toStr = formatLocalYMD(transTo);
console.log("[IrTrFilter] applying with dates:", { fromStr, toStr });
    onApplyFilters({
      locations: selectedLocations,
      outlet_category: selectedOutletCategories,
      pmi_entity_code: selectedPmi,
      outlet_name: selectedOutletNames,
      transaction_from: fromStr,
      transaction_to: toStr,
    });
    onCancel?.();
  };

  return (
    <Box
      sx={{
        width: 420,
        maxHeight: "80vh",
        background: "#fff",
        borderRadius: 2,
        p: 0,
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2.5, borderBottom: "1px solid #eee" }}>
        <Typography sx={{ fontWeight: 600 }}>Apply Filter</Typography>
      </Box>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Locations</Typography>
          <SearchableMultiSelect
            label="Select Locations"
            options={locationOptions}
            value={selectedLocations}
            onChange={setSelectedLocations}
            loading={loadingLocCat}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Category</Typography>
          <SearchableMultiSelect
            label="Select Outlet Category"
            options={outletCategoryOptions}
            value={selectedOutletCategories}
            onChange={setSelectedOutletCategories}
            loading={loadingLocCat}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select PMI Entity Code</Typography>
          <SearchableMultiSelect
            label="Select PMI Entity Code"
            options={pmiOptions}
            value={selectedPmi}
            onChange={setSelectedPmi}
            loading={loadingPmiOutlet}
          />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Select Outlet Names</Typography>
          <SearchableMultiSelect
            label="Select Outlet Names"
            options={outletNameOptions}
            value={selectedOutletNames}
            onChange={setSelectedOutletNames}
            loading={loadingPmiOutlet}
          />
        </Box>

        {/* Transaction Date */}
        <Box sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: 13, mb: 0.8 }}>Transaction Date</Typography>
          <Box sx={{ width: "100%" }}>
            <GlobalStyles
              styles={{
                ".react-datepicker-wrapper": {
                  width: "100% !important",
                },
              }}
            />
            <DatePicker
              selectsRange
              startDate={transFrom ?? undefined}
              endDate={transTo ?? undefined}
              onChange={(range: [Date | null, Date | null]) => {
                const [start, end] = range || [null, null];
                setTransFrom(start ?? null);
                setTransTo(end ?? null);
              }}
              dateFormat="yyyy-MM-dd"
              renderCustomHeader={renderHeaderWithIcons}
              customInput={
                <ForwardedTextInput
                  placeholder={formatRangeText(transFrom, transTo)}
                  key={`txn-${transFrom?.getTime() ?? 0}-${transTo?.getTime() ?? 0}`}
                  InputProps={{
                    endAdornment:
                      transFrom || transTo ? (
                        <img
                          onClick={(e) => {
                            e.stopPropagation();
                            setTransFrom(null);
                            setTransTo(null);
                          }}
                          src={CLEAR_X_DATA_URI}
                          alt="Clear"
                          style={{
                            height: 18,
                            width: 18,
                            cursor: "pointer",
                            marginRight: 10,
                            paddingTop: "4px",
                          }}
                        />
                      ) : undefined,
                  }}
                />
              }
            />
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2.5,
          borderTop: "1px solid #eee",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.25,
        }}
      >
        <Button
          onClick={handleClear}
          sx={{
            border: "1px solid #E7E8E9",
            color: "#000",
            borderRadius: 2,
            height: 40,
          }}
        >
          CLEAR ALL
        </Button>
        <Button
          onClick={handleApply}
          sx={{
            border: "1px solid #C8172F",
            color: "#C8172F",
            borderRadius: 2,
            height: 40,
          }}
        >
          APPLY
        </Button>
      </Box>
    </Box>
  );
}
